// 핸드폰 카메라 바코드 연속 인식 — 쿠패스 /m/putaway(上架·托盘箱号) · /m/inbound-register(手机入仓) 공용(2026-10-07 YZY 「扫码慢」).
// 화면은 video·canvas 와 「지금 찾을 포맷」「잘라 볼 띠(ROI)」만 넘기고, 인식되면 onDetect(값) 를 받는다.
//
// 빨라진 이유(전과 비교):
//  - BarcodeDetector 가 있으면 200ms 타이머 대신 requestAnimationFrame 마다(앞 인식이 끝나야 다음) 연속 인식
//  - 없으면(iOS Safari·위챗 내장·일부 중국산 안드로이드) quagga: 500ms → 끝나는 대로 바로 다음(QUAGGA_GAP_MS),
//    전체 프레임 대신 띠만 800px 로 줄여 JPEG. locate:false(가운데 가로줄만 읽기 — 빠르지만 기울면·배경 잡음이 길면 놓친다)와
//    locate:true(띠 안에서 바코드 위치 찾기)를 번갈아 — 반듯하면 첫 프레임, 기울어도 다음 프레임에 잡는다(로컬 실측 PR 본문)
//  - 단계별 포맷만 찾는다(창고 라벨은 전부 CODE128 — InboundLabel·LocationLabelScan·BoxAssignModal). QR 은 안 찾는다
//  - 같은 값 연속 인식은 dupMs 안에서 한 번만
const ALL_FORMATS = ['code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'itf', 'qr_code'];
const QUAGGA_READERS = {
  code_128: 'code_128_reader',
  code_39: 'code_39_reader',
  ean_13: 'ean_reader',
  ean_8: 'ean_8_reader',
  upc_a: 'upc_reader',
  upc_e: 'upc_e_reader',
  itf: 'i2of5_reader'
};

// 단계별 포맷 — 실제 라벨 기준
export const SCAN_FORMATS = {
  // 위치번호·박스·파렛트 라벨(우리가 찍는 라벨) = CODE128
  LABEL: ['code_128'],
  // 중국 택배 운송장 = CODE128 이 대부분, 일부 CODE39
  WAYBILL: ['code_128', 'code_39'],
  // 上架 상품 바코드 — 쿠팡 바코드(CODE128) + 제조사 EAN/UPC
  PRODUCT: ['code_128', 'code_39', 'ean_13', 'ean_8', 'upc_a', 'upc_e']
};

// 카메라 요청 — 1280 이면 충분하다(디코더에는 어차피 띠만 줄여 넣는다)
export const CAMERA_1280 = { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } };

const DETECTOR_MAX_W = 1280;   // BarcodeDetector 에 넣는 띠 최대 폭
const QUAGGA_MAX_W = 800;      // quagga 에 넣는 띠 최대 폭(quagga 기본 size 와 같다)
const QUAGGA_GAP_MS = 40;      // quagga 연속 인식 사이 쉬는 시간(메인스레드 양보)
const PHOTO_SIZE = 1280;       // 사진 한 장 인식(locate:true) 크기

export default class FastScanner {
  constructor({ tag = '[扫码]', dupMs = 2000, quaggaMaxW = QUAGGA_MAX_W } = {}) {
    this.tag = tag;
    this.quaggaMaxW = quaggaMaxW;   // quagga 에 넣는 띠 최대 폭 — 긴 바코드(박스 라벨 19자)는 크게 줘야 읽는다
    this.dupMs = dupMs;
    this.engine = '';
    this.supported = null;
    this.detectors = {};
    this.quagga = null;
    this.running = false;
    this.raf = 0;
    this.timer = null;
    this.last = { v: '', at: 0 };
    this.flip = false;
    this.opts = null;
    this.canvas = null;
  }

  async ensureEngine() {
    if (this.engine) return this.engine;
    if ('BarcodeDetector' in window) {
      try {
        const sup = window.BarcodeDetector.getSupportedFormats ? await window.BarcodeDetector.getSupportedFormats() : ALL_FORMATS;
        this.supported = ALL_FORMATS.filter(f => sup.includes(f));
      } catch (e) {
        console.error(this.tag, 'BarcodeDetector 포맷 조회 실패 — quagga 로', e);
        this.supported = [];
      }
      if (this.supported.includes('code_128')) {
        this.engine = 'BarcodeDetector';
        return this.engine;
      }
    }
    const mod = await import('quagga');
    this.quagga = mod.default || mod;
    this.engine = 'quagga';
    return this.engine;
  }

  // 포맷 묶음마다 detector 하나(만들기 비싸서 재사용)
  detectorFor(formats) {
    const list = (formats || ALL_FORMATS).filter(f => this.supported.includes(f));
    const use = list.length ? list : this.supported;
    const key = use.join(',');
    if (!this.detectors[key]) this.detectors[key] = new window.BarcodeDetector({ formats: use });
    return this.detectors[key];
  }

  quaggaDecode(src, formats, locate, size) {
    const readers = (formats || ALL_FORMATS).map(f => QUAGGA_READERS[f]).filter(Boolean);
    return new Promise(resolve => {
      this.quagga.decodeSingle({
        src,
        numOfWorkers: 0,
        locate,
        locator: { halfSample: true, patchSize: 'large' },
        inputStream: { size },
        decoder: { readers: readers.length ? readers : ['code_128_reader'] }
      }, res => resolve(res && res.codeResult ? res.codeResult.code : ''));
    });
  }

  // 사진 한 장(dataURL) — 어디에 찍혔는지 모르니 quagga 는 locate:true
  async decodeSrc(src, formats) {
    await this.ensureEngine();
    if (this.engine === 'BarcodeDetector') {
      const img = new Image();
      img.src = src;
      await img.decode();
      const codes = await this.detectorFor(formats).detect(img);
      return codes && codes.length ? codes[0].rawValue : '';
    }
    return this.quaggaDecode(src, formats, true, PHOTO_SIZE);
  }

  // 사진 한 장 — 긴 바코드는 한 해상도로 못 읽는 경우가 많아 여러 해상도·방식으로 차례로 시도한다(quagga 만. BarcodeDetector 는 한 번)
  async decodePhoto(src, formats) {
    await this.ensureEngine();
    if (this.engine === 'BarcodeDetector') return this.decodeSrc(src, formats);
    const tries = [[true, 1280], [true, 1920], [true, 2560], [false, 1920], [false, 2560]];
    for (const [locate, size] of tries) {
      const v = await this.quaggaDecode(src, formats, locate, size);
      if (v) return v;
    }
    return '';
  }

  // opts: { video, canvas?, formats: () => [...], roi?: (video) => { sx, sy, sw, sh }, enabled?: () => bool, onDetect: (v) => void }
  async start(opts) {
    this.stop();
    this.opts = opts;
    this.canvas = opts.canvas || this.canvas || document.createElement('canvas');
    await this.ensureEngine();
    this.running = true;
    this.schedule();
    return this.engine;
  }

  stop() {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    clearTimeout(this.timer);
    this.raf = 0;
    this.timer = null;
  }

  resetDup() {
    this.last = { v: '', at: 0 };
  }

  schedule() {
    if (!this.running) return;
    if (this.engine === 'BarcodeDetector') this.raf = requestAnimationFrame(() => this.tick());
    else this.timer = setTimeout(() => this.tick(), QUAGGA_GAP_MS);
  }

  // 한 번에 하나만 — 앞 인식이 끝나야 다음 프레임을 잡는다
  async tick() {
    if (!this.running) return;
    const o = this.opts;
    try {
      if (!o.enabled || o.enabled()) {
        const v = await this.decodeFrame();
        if (v && this.running) this.emit(v);
      }
    } catch (e) {
      console.error(this.tag, '바코드 인식 오류:', e);
    }
    this.schedule();
  }

  emit(v) {
    const now = Date.now();
    if (v === this.last.v && now - this.last.at < this.dupMs) return;
    this.last = { v, at: now };
    this.opts.onDetect(v);
  }

  async decodeFrame() {
    const video = this.opts.video;
    if (!video || video.readyState < 2 || !video.videoWidth) return '';
    const r = this.opts.roi ? this.opts.roi(video) : { sx: 0, sy: 0, sw: video.videoWidth, sh: video.videoHeight };
    const det = this.engine === 'BarcodeDetector';
    const scale = Math.min(1, (det ? DETECTOR_MAX_W : this.quaggaMaxW) / r.sw);
    const c = this.canvas;
    const w = Math.max(1, Math.round(r.sw * scale));
    const h = Math.max(1, Math.round(r.sh * scale));
    if (c.width !== w) c.width = w;
    if (c.height !== h) c.height = h;
    c.getContext('2d').drawImage(video, r.sx, r.sy, r.sw, r.sh, 0, 0, w, h);
    const formats = this.opts.formats ? this.opts.formats() : null;
    if (det) {
      const codes = await this.detectorFor(formats).detect(c);
      return codes && codes.length ? codes[0].rawValue : '';
    }
    this.flip = !this.flip;
    return this.quaggaDecode(c.toDataURL('image/jpeg', 0.9), formats, this.flip, Math.max(w, h));
  }
}

// 화면 가운데 가로 띠(보이는 화면 기준 비율) — object-fit 없이 전체가 보이는 video 용
export function centerBand(ratio) {
  return video => {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const sh = vh * ratio;
    return { sx: 0, sy: (vh - sh) / 2, sw: vw, sh };
  };
}
